<template>
  <div class="publisher-payout-data">
    <breadcrumbs :breadcrumbsData="breadcrumbsData" />

    <div class="actions">
      <artmisads-button isSquare="true" type="primary" @click="openCreateModal">
        创建任务
      </artmisads-button>
    </div>

    <div class="table-wrap">
      <artmisads-table
        v-model:pagination="pagination"
        :columns="columns"
        :data-source="taskList"
        :loading="loading"
        :change="onTableChange"
        :scroll="{ x: 1000 }"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'billingRange'">
            {{ formatBillingRange(record) }}
          </template>
          <template v-if="column.key === 'status'">
            <span :class="['status-text', getStatusMeta(record.status).className]">
              {{ getStatusMeta(record.status).text }}
            </span>
          </template>
          <template v-if="column.key === 'action'">
            <a
              v-if="canDownload(record)"
              href="javascript:void(0)"
              class="action-link"
              @click="onDownload(record)"
            >
              下载
            </a>
            <a
              v-if="record.status === 'WAITING'"
              href="javascript:void(0)"
              class="action-link"
              @click="onCancelGenerate(record)"
            >
              取消生成
            </a>
            <a 
              v-if="record.status === 'FAILED'"
              href="javascript:void(0)"
              class="action-link"
              @click="onReGenerate(record)"
            >
              重新生成
            </a>
            <a 
              v-if="record.status === 'CANCELLED'"
              href="javascript:void(0)"
              class="action-link"
            >
              --
            </a>
          </template>
        </template>
      </artmisads-table>
    </div>

    <artmisads-modal
      v-model:open="createVisible"
      title="创建任务"
      width="520px"
      :maskClosable="false"
      :keyboard="false"
      :onCancel="closeCreateModal"
      destroyOnClose
    >
      <a-form
        ref="createFormRef"
        :model="createForm"
        :label-col="{ span: 24 }"
        :wrapper-col="{ span: 24 }"
        autocomplete="off"
      >
        <a-form-item
          label="文件名"
          name="fileName"
          :rules="[{ required: true, message: '请输入文件名' }]"
        >
          <a-input v-model:value="createForm.fileName" placeholder="请输入文件名" allow-clear />
        </a-form-item>
        <a-form-item
          label="账单时间范围"
          name="dateRange"
          :rules="[{ required: true, message: '请选择账单时间范围' }]"
        >
          <artemisads-range-picker
            v-model:value="createForm.dateRange"
            picker="month"
            style="width: 100%"
            :disabled-date="disabledDate"
            format="YYYY-MM"
          />
        </a-form-item>
      </a-form>
      <template #footer>
        <artmisads-button @click="closeCreateModal">取消</artmisads-button>
        <artmisads-button type="primary" :loading="creating" @click="onCreateTask">
          创建任务
        </artmisads-button>
      </template>
    </artmisads-modal>
  </div>
</template>

<script setup>
import { onMounted, reactive, ref } from 'vue'
import moment from 'moment'
import { message } from 'ant-design-vue'
import breadcrumbs from '@/components/breadcrumbs.vue'
import { financeApi } from '~/server/operator/finance'

const breadcrumbsData = ref({
  title: 'Publisher Payout Data',
  des: '',
  links: [
    {
      name: 'Home',
      path: '/operator/users',
    },
    {
      name: 'Finance',
      path: '/operator/finance/payments',
    },
    {
      name: 'Publisher Payout Data',
      path: '/operator/finance/publisherPayoutData',
    },
  ],
})

const columns = [
  {
    title: '创建时间',
    dataIndex: 'createdAt',
    key: 'createdAt',
    align: 'center',
  },
  {
    title: '文件名',
    dataIndex: 'fileName',
    key: 'fileName',
    align: 'center',
  },
  {
    title: '账单时间范围',
    dataIndex: 'billingRange',
    key: 'billingRange',
    align: 'center',
  },
  {
    title: '状态',
    dataIndex: 'status',
    key: 'status',
    align: 'center',
  },
  {
    title: '操作人',
    dataIndex: 'operatorName',
    key: 'operatorName',
    align: 'center',
  },
  {
    title: '操作',
    dataIndex: 'action',
    key: 'action',
    align: 'center',
  },
]

const STATUS_META = {
  WAITING: { text: '等待中', className: 'status-waiting' },
  GENERATING: { text: '生成中', className: 'status-processing' },
  GENERATED: { text: '已生成', className: 'status-success' },
  FAILED: { text: '生成失败', className: 'status-failed' },
  CANCELLED: { text: '已取消', className: 'status-cancelled' },
}

const loading = ref(false)
const creating = ref(false)
const createVisible = ref(false)
const createFormRef = ref(null)
const taskList = ref([])
const pagination = reactive({
  current: 1,
  defaultPageSize: 10,
  size: 10,
  pageSize: 10,
  total: 0,
  pageSizeOptions: ['10', '20', '50', '100'],
  showSizeChanger: true,
})

const getDefaultDateRange = () => [
  moment().subtract(35, 'month').startOf('month'),
  moment().endOf('month'),
]

const createForm = reactive({
  fileName: '',
  dateRange: getDefaultDateRange(),
})

const normalizeTimestamp = (value) => {
  if (!value) return '-'
  const timestamp = Number(value)
  if (!Number.isNaN(timestamp)) {
    const ms = timestamp < 1000000000000 ? timestamp * 1000 : timestamp
    return moment(ms).format('YYYY.MM.DD HH:mm:ss')
  }
  const parsed = moment(value)
  return parsed.isValid() ? parsed.format('YYYY.MM.DD HH:mm:ss') : '-'
}

const formatBillingRange = (record) => {
  const start = record.startMonth || record.billingStartDate || record.startDate
  const end = record.endMonth || record.billingEndDate || record.endDate
  if (!start || !end) return record.billingRange || '-'
  return `${moment(start).format('YYYY.MM')} - ${moment(end).format('YYYY.MM')}`
}

const getStatusMeta = (status) => {
  if (!status) return { text: '-', className: 'status-waiting' }
  const key = String(status).toUpperCase()
  if (STATUS_META[key]) return STATUS_META[key]
  if (String(status) === '已生成') return STATUS_META.GENERATED
  return { text: status, className: 'status-waiting' }
}

const canDownload = (record) => {
  const status = String(record.status || '').toUpperCase()
  return status === 'GENERATED' || record.status === '已生成'
}

const disabledDate = (current) => {
  if (!current) return false
  if (current.isAfter(moment(), 'month')) return true
  const range = createForm.dateRange
  if (!range || range.length === 0) return false
  const tooLate = range[0] && current.diff(range[0], 'days') > 365 * 3
  const tooEarly = range[1] && range[1].diff(current, 'days') > 365 * 3
  return tooEarly || tooLate
}

const fetchTaskList = async (params = {}) => {
  loading.value = true
  if (Object.keys(params).length === 0) {
    pagination.current = 1
  }
  const finalParams = {
    page: pagination.current,
    size: pagination.size,
    ...params,
  }
  try {
    const res = await financeApi.getPublisherPayoutTasks(finalParams)
    const list = res?.data?.data || res?.data || []
    const total = res?.data?.total ?? res?.total ?? 0
    taskList.value = (Array.isArray(list) ? list : []).map((item) => ({
      ...item,
      key: item.id,
      createdAt: normalizeTimestamp(item.createdAt),
      operatorName: item.operatorName || item.operator || '-',
    }))
    pagination.total = total
  } finally {
    loading.value = false
  }
}

const onTableChange = ({ current, pageSize }) => {
  pagination.current = current
  pagination.size = pageSize
  pagination.pageSize = pageSize
  fetchTaskList({ page: current, size: pageSize })
}

const openCreateModal = () => {
  createForm.fileName = ''
  createForm.dateRange = getDefaultDateRange()
  createVisible.value = true
}

const closeCreateModal = () => {
  createVisible.value = false
}

const onCreateTask = () => {
  createFormRef.value?.validate().then(async () => {
    const [start, end] = createForm.dateRange || []
    if (!start || !end) {
      message.error('请选择账单时间范围')
      return
    }
    creating.value = true
    try {
      const payload = {
        fileName: createForm.fileName.trim(),
        startMonth: start.clone().startOf('month').format('YYYY-MM'),
        endMonth: end.clone().endOf('month').format('YYYY-MM'),
      }
      const res = await financeApi.createPublisherPayoutTask(payload)
      if (res?.success === false) {
        message.error(res.message || res.error || '创建失败')
        return
      }
      message.success('创建任务成功')
      createVisible.value = false
      fetchTaskList({})
    } finally {
      creating.value = false
    }
  })
}


const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.style.position = 'fixed'
    textarea.style.left = '-9999px'
    document.body.appendChild(textarea)
    textarea.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(textarea)
    return ok
  }
}
const normalizeFileUrl = (url) => {
  const trimmed = String(url || '').trim()
  if (!trimmed) return ''
  if (/^https?:\/\//i.test(trimmed)) return trimmed
  if (trimmed.startsWith('//')) return `${window.location.protocol}${trimmed}`
  return `${window.location.origin}${trimmed.startsWith('/') ? '' : '/'}${trimmed}`
}

const onDownload = async (record) => {
  const fileUrl = normalizeFileUrl(record.fileUrl)
  if (!fileUrl) {
    message.error('文件不存在')
    return
  }
  const copied = await copyToClipboard(fileUrl)
  if (copied) {
    message.success('下载链接已复制，请在浏览器地址栏粘贴打开下载')
  } else {
    message.warning(`复制失败，请手动复制链接：${fileUrl}`)
  }
}
const onCancelGenerate = (record) => {
  financeApi.cancelPublisherPayoutTask({ id: record.id }).then(res => {
    if (res?.success === false) {
      message.error(res.message || res.error || '取消生成失败')
      return
    }
    message.success('取消生成成功')
    fetchTaskList({})
  })
}
const onReGenerate = (record) => {
  financeApi.retryPublisherPayoutTask({ id: record.id }).then(res => {
    if (res?.success === false) {
      message.error(res.message || res.error || '重新生成失败')
      return
    }
    message.success('重新生成成功')
    fetchTaskList({})
  })
}
onMounted(() => {
  fetchTaskList({})
})
</script>

<style scoped lang="less">
.publisher-payout-data {
  width: 100%;
  padding: 16px;
  padding-top: unset;
}

.actions {
  display: flex;
  justify-content: flex-end;
  margin: 16px 0;
}

.table-wrap {
  padding: 15px;
  overflow: hidden;
  border: 1px solid #ffffff;
  border-radius: 12px;
  background-color: var(--color-white);

  :deep(.ant-table) {
    border-radius: 8px 8px 0 0;
  }
}

.status-text {
  font-weight: 500;
}

.status-waiting {
  color: #8c8c8c;
}

.status-processing {
  color: #7653da;
}

.status-success {
  color: #7653da;
}

.status-failed {
  color: #eb4e3d;
}

.status-cancelled {
  color: #eb4e3d;
}

.action-link {
  color: var(--primary);

  &:hover {
    color: var(--primary-hover);
  }
}

.action-placeholder {
  color: var(--dark-3);
}
</style>
